/** The narrow Web Crypto surface also permits deterministic entropy in unit tests. */
export interface RandomSource {
  getRandomValues(bytes: Uint8Array<ArrayBuffer>): Uint8Array<ArrayBuffer>;
  randomUUID?(): string;
}

const base36Alphabet = '0123456789abcdefghijklmnopqrstuvwxyz';
// Matches generate-password-browser's default letter pool + excludeSimilarCharacters: true.
const passwordAlphabet = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ';

function secureSource(source?: RandomSource): RandomSource {
  const resolved = source ?? globalThis.crypto;
  if (typeof resolved?.getRandomValues !== 'function') {
    throw new Error('Web Crypto is required to generate secure random values.');
  }
  return resolved;
}

function randomCharacters(
  length: number,
  alphabet: string,
  source: RandomSource,
): string {
  // Discard the incomplete final bucket instead of biasing characters with byte % alphabet.length.
  const limit = 256 - (256 % alphabet.length);
  const bytes = new Uint8Array(32);
  let result = '';
  while (result.length < length) {
    source.getRandomValues(bytes);
    for (const byte of bytes) {
      if (byte >= limit) continue;
      result += alphabet[byte % alphabet.length];
      if (result.length === length) break;
    }
  }
  return result;
}

/**
 * Random identifier with the legacy QR/GraphQL-compatible shape: c + 24 lowercase base36 characters.
 * This is NOT CUID: it carries no timestamp, counter or fingerprint and must not be sorted as creation time.
 * Persist explicit creation timestamps before replacing CUID in a chronologically sorted collection.
 */
export function createCompatibleRandomId(source?: RandomSource): string {
  return 'c' + randomCharacters(24, base36Alphabet, secureSource(source));
}

/** UUID v4; getRandomValues also works on HTTP LAN origins where randomUUID is unavailable. */
export function createRandomUuid(source?: RandomSource): string {
  const crypto = secureSource(source);
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  // RFC 9562 section 5.4: version 0100 and variant 10, retaining 122 random bits.
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'));
  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10).join(''),
  ].join('-');
}

/** Preserves the existing 15-letter password policy, including excluded ambiguous characters. */
export function createSharePassword(source?: RandomSource): string {
  return randomCharacters(15, passwordAlphabet, secureSource(source));
}
